import { useState, useEffect, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { fmtEUR, creditsLabel, fmtTimestamp } from "@/lib/format";
import { fmtDuration } from "@/lib/agenda";
import { BRANDING } from "@/config/branding";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, Pencil, X, Download, Upload, Archive, RotateCcw, Building2, Wrench, CreditCard, ScrollText, Database, CalendarCheck2, UserPlus, Users, Lock, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { RELEASE } from "@/config/release";
import { toast } from "sonner";
import { GoogleCalendarPanel } from "@/components/GoogleCalendarPanel";
import { InstallSettings } from "@/components/pwa/InstallSettings";
import { InviteManager } from "@/components/InviteManager";
import { Link } from "react-router-dom";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { useAuth } from "@/context/AuthContext";

const CATEGORIES = ["Educazione", "Passeggiate", "Asilo", "Pensione", "Consulenza", "Trasporto", "Altro"];

const DUR_FACTOR = { minutes: 1, hours: 60, days: 1440 };
const SERVICE_DUR_PRESETS = [
  [15, "minutes"], [30, "minutes"], [45, "minutes"],
  [60, "hours"], [90, "hours"], [120, "hours"], [180, "hours"], [240, "hours"],
  [360, "hours"], [480, "hours"], [600, "hours"], [720, "hours"], [1440, "days"],
];
const svcDurLabel = (m) => (Number(m) ? fmtDuration(Number(m)) : "Nessuna durata");
const BILLING_LABEL = { minute: "minuto", hour: "ora", day: "giorno" };
const presetKey = (m, u) => `${m}:${u}`;
const isPreset = (m, u) => SERVICE_DUR_PRESETS.some(([pm, pu]) => pm === m && pu === u);
const deriveDur = (t) => {
  const has = t.has_duration !== false;
  const u = t.duration_unit || "minutes";
  const m = Number(t.default_duration_minutes || 0);
  return { has_duration: has, duration_unit: u, dur_value: has ? m / (DUR_FACTOR[u] || 1) : 1 };
};

const AUDIT_LABELS = {
  service_type_created: "Servizio creato",
  service_type_deleted: "Servizio eliminato definitivamente",
  service_type_edited: "Servizio modificato",
  service_created: "Prestazione registrata",
  service_status_changed: "Stato prestazione modificato",
  service_cancelled: "Prestazione annullata",
  credits_consumed: "Crediti scalati",
  credits_restored: "Crediti ripristinati",
  payment_created: "Pagamento registrato",
  payment_deleted: "Pagamento eliminato",
  package_created: "Pacchetto creato",
  package_edited: "Pacchetto modificato",
  settings_changed: "Impostazioni aggiornate",
  backup_imported: "Backup importato",
  dog_created: "Cane creato",
  dog_edited: "Dati cane modificati",
  dog_health_updated: "Informazioni sanitarie aggiornate",
  dog_deleted: "Cane eliminato",
  owner_deleted: "Cliente eliminato",
  dog_archived: "Percorso cane archiviato",
  dog_reactivated: "Percorso cane riattivato",
  owner_archived: "Cliente archiviato",
  owner_reactivated: "Cliente riattivato",
  contact_created: "Contatto creato",
  contact_stage_changed: "Stato contatto modificato",
  contact_converted: "Contatto convertito in cliente",
  follow_up_set: "Follow-up aggiornato",
  document_created: "Documento/consenso creato",
  document_updated: "Documento/consenso aggiornato",
  document_deleted: "Documento/consenso eliminato",
  media_consent_revoked: "Consenso media revocato",
  service_deleted: "Prestazione eliminata",
  login: "Accesso effettuato",
  logout: "Logout effettuato",
  invite_created: "Invito creato",
  invite_accepted: "Invito accettato",
  invite_revoked: "Invito revocato",
  invite_deleted: "Invito eliminato",
  tester_disabled: "Tester disabilitato",
  tester_reactivated: "Tester riattivato",
  password_changed: "Password aggiornata",
};

const COLL_LABELS = {
  owners: "Proprietari", dogs: "Cani", sessions: "Prestazioni", packages: "Pacchetti",
  payments: "Pagamenti", service_types: "Tipi di servizio", settings: "Impostazioni",
  audit_logs: "Registro attività", migrations: "Migrazioni",
};

const Section = ({ icon: Icon, title, children, action }) => (
  <section className="rounded-2xl border border-border bg-card p-5">
    <div className="flex items-center justify-between mb-3">
      <h2 className="font-head text-lg font-bold flex items-center gap-2"><Icon className="h-5 w-5 text-primary" />{title}</h2>
      {action}
    </div>
    {children}
  </section>
);

export default function SettingsPage() {
  const { user } = useAuth();
  const [types, setTypes] = useState([]);
  const [settings, setSettings] = useState({ payment_methods: [], business: {} });
  const [newMethod, setNewMethod] = useState("");
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState(null);
  const [biz, setBiz] = useState({});
  const [audit, setAudit] = useState([]);
  const [importData, setImportData] = useState(null);
  const [importSummary, setImportSummary] = useState(null);
  const [durMode, setDurMode] = useState("preset");
  const [delType, setDelType] = useState(null);

  const load = useCallback(() => {
    api.get("/service-types").then((r) => setTypes(r.data)).catch(() => {});
    api.get("/settings").then((r) => { setSettings(r.data); setBiz(r.data.business || {}); }).catch(() => {});
    api.get("/audit", { params: { limit: 100 } }).then((r) => setAudit(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const methods = settings.payment_methods || [];

  // ---- service catalogue ----
  const openNew = () => { setDurMode("preset"); setForm({ name: "", description: "", category: "Altro", default_price: 0, consumes: false, default_credits: 0, is_package_type: false, active: true, has_duration: true, duration_unit: "hours", dur_value: 1, pricing_mode: "fixed", billing_unit: null, price_scope: "per_booking", professional_diary_enabled: true }); setDlg(true); };
  const openEdit = (t) => { const d = deriveDur(t); setDurMode(!d.has_duration ? "none" : (isPreset(Number(t.default_duration_minutes || 0), d.duration_unit) ? "preset" : "custom")); setForm({ ...t, consumes: (t.default_credits || 0) > 0, ...d, pricing_mode: t.pricing_mode || "fixed", billing_unit: t.billing_unit || null, price_scope: t.price_scope || "per_booking", professional_diary_enabled: t.professional_diary_enabled !== false }); setDlg(true); };

  const saveType = async () => {
    if (!form.name.trim()) return toast.error("Nome obbligatorio.");
    const credits = form.consumes ? Math.max(1, Number(form.default_credits) || 1) : 0;
    const hasDur = !!form.has_duration;
    const minutes = hasDur ? Math.round(Number(form.dur_value || 0) * (DUR_FACTOR[form.duration_unit] || 1)) : 0;
    if (hasDur && minutes <= 0) return toast.error("La durata deve essere positiva.");
    if (hasDur && form.duration_unit === "days" && !Number.isInteger(Number(form.dur_value))) return toast.error("I giorni devono essere interi.");
    const pricingMode = hasDur ? (form.pricing_mode || "fixed") : "fixed";
    if (pricingMode === "per_time_unit" && !form.billing_unit) return toast.error("Scegli l'unità di fatturazione.");
    const payload = {
      name: form.name, description: form.description || "", category: form.category || "Altro",
      default_price: Number(form.default_price), default_duration_minutes: minutes,
      default_credits: credits, is_package_type: !!form.is_package_type, active: form.active !== false,
      has_duration: hasDur, duration_unit: hasDur ? form.duration_unit : "minutes",
      pricing_mode: pricingMode, billing_unit: pricingMode === "per_time_unit" ? form.billing_unit : null,
      price_scope: form.price_scope || "per_booking",
      package_eligible: !!form.consumes,
      professional_diary_enabled: form.professional_diary_enabled !== false,
    };
    try {
      if (form.id) await api.put(`/service-types/${form.id}`, payload);
      else await api.post("/service-types", payload);
      toast.success("Salvato"); setDlg(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const setTypeActive = async (t, active) => {    try {
      await api.put(`/service-types/${t.id}`, {
        name: t.name, description: t.description || "", category: t.category || "Altro",
        default_price: t.default_price, default_duration_minutes: t.default_duration_minutes || 0,
        default_credits: t.default_credits || 0, is_package_type: !!t.is_package_type, active,
        has_duration: t.has_duration !== false, duration_unit: t.duration_unit || "minutes",
        pricing_mode: t.pricing_mode || "fixed", billing_unit: t.billing_unit || null, price_scope: t.price_scope || "per_booking",
        package_eligible: t.package_eligible !== false, professional_diary_enabled: t.professional_diary_enabled !== false,
      });
      toast.success(active ? "Riattivato" : "Disattivato"); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const deleteType = async () => {
    const t = delType;
    setDelType(null);
    try {
      await api.delete(`/service-types/${t.id}`);
      toast.success("Servizio eliminato"); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  // ---- payment methods ----
  const saveMethods = async (list) => {
    try { await api.put("/settings", { payment_methods: list }); setSettings((s) => ({ ...s, payment_methods: list })); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const addMethod = () => {
    const n = newMethod.trim();
    if (!n || methods.some((m) => m.name === n)) return;
    saveMethods([...methods, { name: n, active: true }]); setNewMethod("");
  };
  const toggleMethod = (name, active) => saveMethods(methods.map((m) => (m.name === name ? { ...m, active } : m)));
  const removeMethod = (name) => saveMethods(methods.filter((m) => m.name !== name));

  // ---- business profile ----
  const saveBiz = async () => {
    try { await api.put("/settings", { business: biz }); toast.success("Profilo attività salvato"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  // ---- backup ----
  const exportBackup = async () => {
    try {
      const r = await api.get("/backup/export");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `k9manager-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click(); URL.revokeObjectURL(url);
      toast.success("Backup esportato");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      const r = await api.post("/backup/validate", json);
      setImportData(json); setImportSummary(r.data);
    } catch (err) { toast.error("File non valido o illeggibile."); }
    e.target.value = "";
  };
  const confirmImport = async () => {
    try {
      const r = await api.post("/backup/import", importData);
      toast.success(`Import completato`);
      setImportData(null); setImportSummary(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const activeTypes = types.filter((t) => t.active !== false);
  const archivedTypes = types.filter((t) => t.active === false);
  const totalNew = importSummary
    ? Object.keys(importSummary.summary || {}).reduce(
        (s, k) => s + Math.max(0, (importSummary.summary[k] || 0) - (importSummary.conflicts[k] || 0) - (importSummary.invalid[k] || 0)),
        0)
    : 0;

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-head text-3xl font-extrabold tracking-tight">Impostazioni</h1>
        <p className="text-sm text-muted-foreground">{BRANDING.productName} {BRANDING.productSignature}</p>
      </div>

      {/* Profilo attività */}
      <Section icon={Building2} title="Profilo attività">
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1 sm:col-span-2"><Label>Nome attività</Label><Input data-testid="biz-name" value={biz.business_name || ""} onChange={(e) => setBiz((b) => ({ ...b, business_name: e.target.value }))} placeholder="Es. Centro Cinofilo Rossi" /></div>
          <div className="space-y-1"><Label>Telefono</Label><Input data-testid="biz-phone" value={biz.phone || ""} onChange={(e) => setBiz((b) => ({ ...b, phone: e.target.value }))} /></div>
          <div className="space-y-1"><Label>Email</Label><Input data-testid="biz-email" value={biz.email || ""} onChange={(e) => setBiz((b) => ({ ...b, email: e.target.value }))} /></div>
          <div className="space-y-1"><Label>Indirizzo</Label><Input data-testid="biz-address" value={biz.address || ""} onChange={(e) => setBiz((b) => ({ ...b, address: e.target.value }))} /></div>
          <div className="space-y-1"><Label>Città / zona</Label><Input data-testid="biz-city" value={biz.city || ""} onChange={(e) => setBiz((b) => ({ ...b, city: e.target.value }))} /></div>
          <div className="space-y-1"><Label>P.IVA / C.F.</Label><Input data-testid="biz-vat" value={biz.vat || ""} onChange={(e) => setBiz((b) => ({ ...b, vat: e.target.value }))} /></div>
          <div className="space-y-1 sm:col-span-2"><Label>Note</Label><Textarea data-testid="biz-notes" value={biz.notes || ""} onChange={(e) => setBiz((b) => ({ ...b, notes: e.target.value }))} /></div>
        </div>
        <div className="mt-3"><Button data-testid="biz-save" onClick={saveBiz} className="rounded-full">Salva profilo</Button></div>
      </Section>

      <InstallSettings />

      <Section icon={Lock} title="Sicurezza account">
        <p className="text-xs text-muted-foreground mb-3">
          Cambia la password del tuo account. Per sicurezza, dopo l'aggiornamento verrai disconnesso da tutti i dispositivi e dovrai accedere di nuovo.
        </p>
        <ChangePasswordDialog />
      </Section>

      {user?.is_platform_admin && (
        <Section icon={UserPlus} title="Inviti beta">
          <InviteManager />
        </Section>
      )}

      {user?.is_platform_admin && (
        <Section icon={Users} title="Amministrazione K9">
          <Link to="/admin/support" className="underline font-semibold">Apri Admin / Support</Link>
        </Section>
      )}

      {/* Tipi di servizio e prezzi */}
      <Section icon={Wrench} title="Tipi di servizio e prezzi" action={<Button size="sm" data-testid="add-type-btn" onClick={openNew} className="rounded-full"><Plus className="h-4 w-4 mr-1" />Aggiungi servizio</Button>}>
        <p className="text-xs text-muted-foreground mb-3">Le modifiche ai prezzi predefiniti valgono solo per i nuovi record. Lo storico resta invariato.</p>
        <div className="space-y-2" data-testid="types-list">
          {activeTypes.map((t) => (
            <div key={t.id} className="flex items-center gap-3 rounded-xl border border-border p-3">
              <div className="flex-1 min-w-0">
                <div className="font-semibold flex items-center gap-2 flex-wrap">
                  {t.name}
                  {t.category && <span className="text-[10px] rounded-full bg-secondary px-2 py-0.5">{t.category}</span>}
                  {t.is_package_type && <span className="text-[10px] rounded-full bg-primary/10 text-primary px-2 py-0.5">Pacchetto</span>}
                </div>
                <div className="text-sm text-muted-foreground">
                  {fmtEUR(t.default_price)}{t.pricing_mode === "per_time_unit" && t.billing_unit ? ` / ${BILLING_LABEL[t.billing_unit]}` : ""}{t.has_duration === false ? " · Nessuna durata" : (t.default_duration_minutes ? ` · ${svcDurLabel(t.default_duration_minutes, t.duration_unit)}` : "")}{t.price_scope === "per_dog" ? " · per cane" : ""} · {t.is_package_type ? `${creditsLabel(t.default_credits)} inclusi` : (t.default_credits || 0) > 0 ? `consuma ${creditsLabel(t.default_credits)}` : "no crediti"}
                </div>
              </div>
              <Button size="sm" variant="outline" data-testid={`edit-type-${t.id}`} onClick={() => openEdit(t)} className="rounded-full"><Pencil className="h-4 w-4" /></Button>
              <Button size="sm" variant="ghost" data-testid={`disable-type-${t.id}`} onClick={() => setTypeActive(t, false)} className="rounded-full" title="Disattiva"><Archive className="h-4 w-4" /></Button>
              <Button size="sm" variant="ghost" data-testid={`delete-type-${t.id}`} onClick={() => setDelType(t)} className="rounded-full text-destructive hover:text-destructive" title="Elimina definitivamente"><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
        {archivedTypes.length > 0 && (
          <div className="mt-4">
            <div className="text-xs font-bold text-muted-foreground mb-2">Archiviati</div>
            <div className="space-y-2">
              {archivedTypes.map((t) => (
                <div key={t.id} className="flex items-center gap-3 rounded-xl border border-dashed border-border p-3 opacity-70">
                  <div className="flex-1 min-w-0"><div className="font-semibold">{t.name}</div><div className="text-sm text-muted-foreground">{fmtEUR(t.default_price)}</div></div>
                  <Button size="sm" variant="outline" data-testid={`enable-type-${t.id}`} onClick={() => setTypeActive(t, true)} className="rounded-full"><RotateCcw className="h-4 w-4 mr-1" />Riattiva</Button>
                  <Button size="sm" variant="ghost" data-testid={`delete-type-${t.id}`} onClick={() => setDelType(t)} className="rounded-full text-destructive hover:text-destructive" title="Elimina definitivamente"><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          </div>
        )}
        <AlertDialog open={!!delType} onOpenChange={(o) => !o && setDelType(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Eliminare definitivamente il servizio?</AlertDialogTitle>
              <AlertDialogDescription>Stai per eliminare definitivamente «{delType?.name}». Questa operazione non può essere annullata.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="delete-type-cancel">Annulla</AlertDialogCancel>
              <AlertDialogAction data-testid="delete-type-confirm" onClick={deleteType} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Elimina definitivamente</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </Section>

      {/* Metodi di pagamento */}
      <Section icon={CreditCard} title="Metodi di pagamento">
        <div className="space-y-2 mb-3" data-testid="methods-list">
          {methods.map((m) => (
            <div key={m.name} className={`flex items-center gap-3 rounded-xl border border-border p-3 ${m.active === false ? "opacity-60" : ""}`}>
              <span className="flex-1 font-semibold">{m.name}{m.active === false && <span className="ml-2 text-xs text-muted-foreground">(disattivo)</span>}</span>
              <div className="flex items-center gap-2">
                <Label className="text-xs text-muted-foreground">Attivo</Label>
                <Switch data-testid={`method-active-${m.name}`} checked={m.active !== false} onCheckedChange={(v) => toggleMethod(m.name, v)} />
              </div>
              <button data-testid={`del-method-${m.name}`} onClick={() => removeMethod(m.name)} className="text-muted-foreground hover:text-destructive"><X className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        <div className="flex gap-2 max-w-sm">
          <Input data-testid="new-method" value={newMethod} onChange={(e) => setNewMethod(e.target.value)} placeholder="Nuovo metodo…" />
          <Button data-testid="add-method-btn" onClick={addMethod} className="rounded-full">Aggiungi</Button>
        </div>
      </Section>

      {/* Backup dati */}
      <Section icon={CalendarCheck2} title="Integrazioni · Google Calendar">
        <GoogleCalendarPanel />
      </Section>

      <Section icon={Database} title="Backup dati">
        <p className="text-xs text-muted-foreground mb-3">Esporta un backup JSON completo dei tuoi dati.</p>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="backup-export" onClick={exportBackup} className="rounded-full"><Download className="h-4 w-4 mr-1" />Esporta backup</Button>
        </div>
        <p className="text-xs text-muted-foreground mt-3" data-testid="backup-import-disabled">Import backup temporaneamente non disponibile.</p>
      </Section>

      {/* Registro attività */}
      <Section icon={ScrollText} title="Registro attività">
        <div className="space-y-2 max-h-96 overflow-y-auto wg-scroll" data-testid="audit-list">
          {audit.length === 0 && <p className="text-sm text-muted-foreground">Nessuna attività registrata.</p>}
          {audit.map((a) => (
            <div key={a.id} className="rounded-xl border border-border p-2.5 text-sm">
              <div className="font-semibold">{AUDIT_LABELS[a.action] || a.action}</div>
              {a.description && <div className="text-muted-foreground text-xs mt-0.5 break-words">{a.description}</div>}
              <div className="text-[11px] text-muted-foreground font-mono mt-1">{fmtTimestamp(a.timestamp)}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* Service type dialog */}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto wg-scroll">
          <DialogHeader><DialogTitle className="font-head">{form?.id ? "Modifica servizio" : "Nuovo servizio"}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-3">
              <div className="space-y-1"><Label>Nome</Label><Input data-testid="type-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></div>
              <div className="space-y-1"><Label>Descrizione</Label><Textarea data-testid="type-desc" value={form.description || ""} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Categoria</Label>
                  <Select value={form.category || "Altro"} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                    <SelectTrigger data-testid="type-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label>Durata</Label>
                  <Select
                    value={durMode === "none" ? "none" : durMode === "custom" ? "__custom__" : presetKey(Math.round(Number(form.dur_value || 0) * (DUR_FACTOR[form.duration_unit] || 1)), form.duration_unit)}
                    onValueChange={(v) => {
                      if (v === "none") { setDurMode("none"); setForm((f) => ({ ...f, has_duration: false, pricing_mode: "fixed", billing_unit: null })); }
                      else if (v === "__custom__") { setDurMode("custom"); setForm((f) => ({ ...f, has_duration: true })); }
                      else { const [m, u] = v.split(":"); setDurMode("preset"); setForm((f) => ({ ...f, has_duration: true, duration_unit: u, dur_value: Number(m) / (DUR_FACTOR[u] || 1) })); }
                    }}>
                    <SelectTrigger data-testid="type-duration"><SelectValue placeholder="Durata…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nessuna durata</SelectItem>
                      {SERVICE_DUR_PRESETS.map(([m, u]) => <SelectItem key={presetKey(m, u)} value={presetKey(m, u)}>{svcDurLabel(m, u)}</SelectItem>)}
                      <SelectItem value="__custom__">Durata personalizzata</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {durMode === "custom" && (
                <div className="flex items-end gap-2" data-testid="type-duration-custom-wrap">
                  <div className="space-y-1 flex-1"><Label>Valore</Label>
                    <Input type="number" min="1" inputMode="numeric" data-testid="type-duration-custom" value={form.dur_value} onChange={(e) => setForm((f) => ({ ...f, dur_value: e.target.value }))} /></div>
                  <div className="space-y-1 w-28"><Label>Unità</Label>
                    <Select value={form.duration_unit} onValueChange={(v) => setForm((f) => ({ ...f, duration_unit: v, dur_value: "" }))}>
                      <SelectTrigger data-testid="type-duration-unit"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="minutes">Minuti</SelectItem>
                        <SelectItem value="hours">Ore</SelectItem>
                        <SelectItem value="days">Giorni</SelectItem>
                      </SelectContent>
                    </Select></div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Prezzo predefinito €</Label><Input type="number" data-testid="type-price" value={form.default_price} onChange={(e) => setForm((f) => ({ ...f, default_price: e.target.value }))} /></div>
                {form.consumes && <div className="space-y-1"><Label>Crediti consumati</Label><Input type="number" data-testid="type-credits" value={form.default_credits} onChange={(e) => setForm((f) => ({ ...f, default_credits: e.target.value }))} /></div>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Modalità prezzo</Label>
                  <Select value={form.has_duration ? (form.pricing_mode || "fixed") : "fixed"} onValueChange={(v) => setForm((f) => ({ ...f, pricing_mode: v, billing_unit: v === "per_time_unit" ? (f.billing_unit || "hour") : null }))} disabled={!form.has_duration}>
                    <SelectTrigger data-testid="type-pricing-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">Prezzo fisso</SelectItem>
                      <SelectItem value="per_time_unit" disabled={!form.has_duration}>Prezzo per unità di tempo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.has_duration && form.pricing_mode === "per_time_unit" && (
                  <div className="space-y-1"><Label>Prezzo per</Label>
                    <Select value={form.billing_unit || "hour"} onValueChange={(v) => setForm((f) => ({ ...f, billing_unit: v }))}>
                      <SelectTrigger data-testid="type-billing-unit"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="minute">minuto</SelectItem>
                        <SelectItem value="hour">ora</SelectItem>
                        <SelectItem value="day">giorno</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              {!form.has_duration && <p className="text-[11px] text-muted-foreground -mt-1">Un servizio senza durata usa sempre il prezzo fisso.</p>}
              <div className="space-y-1"><Label>Applica il prezzo</Label>
                <Select value={form.price_scope || "per_booking"} onValueChange={(v) => setForm((f) => ({ ...f, price_scope: v }))}>
                  <SelectTrigger data-testid="type-price-scope"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="per_booking">alla prestazione</SelectItem>
                    <SelectItem value="per_dog">per cane</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between rounded-xl border border-border p-3">
                <div><Label>Utilizzabile con pacchetto</Label><p className="text-[11px] text-muted-foreground">Consente la copertura con crediti del pacchetto.</p></div>
                <Switch data-testid="type-consumes" checked={!!form.consumes} onCheckedChange={(v) => setForm((f) => ({ ...f, consumes: v, default_credits: v ? (Number(f.default_credits) || 1) : 0 }))} />
              </div>
              {form.consumes && (
                <div className="space-y-1"><Label>Crediti predefiniti</Label>
                  <Input type="number" min="1" data-testid="type-default-credits" value={form.default_credits} onChange={(e) => setForm((f) => ({ ...f, default_credits: e.target.value }))} />
                  <p className="text-[11px] text-muted-foreground">Crediti scalati per cane quando coperto da pacchetto.</p>
                </div>
              )}
              <div className="flex items-center justify-between rounded-xl border border-border p-3">
                <div><Label>Prevede Diario professionale</Label><p className="text-[11px] text-muted-foreground">Abilita la compilazione del diario della lezione.</p></div>
                <Switch data-testid="type-diary-enabled" checked={form.professional_diary_enabled !== false} onCheckedChange={(v) => setForm((f) => ({ ...f, professional_diary_enabled: v }))} />
              </div>
              <div className="flex items-center justify-between rounded-xl border border-border p-3">
                <Label>È un pacchetto</Label>
                <Switch data-testid="type-is-package" checked={!!form.is_package_type} onCheckedChange={(v) => setForm((f) => ({ ...f, is_package_type: v }))} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)}>Annulla</Button>
            <Button data-testid="type-save" onClick={saveType}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import dry-run confirmation */}
      <Dialog open={!!importSummary} onOpenChange={(v) => { if (!v) { setImportSummary(null); setImportData(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-head">Conferma importazione</DialogTitle>
            <DialogDescription>Anteprima (dry-run). Nessun dato è stato ancora scritto.</DialogDescription>
          </DialogHeader>
          {importSummary && (
            <div className="space-y-2 text-sm" data-testid="import-summary">
              {Object.keys(importSummary.summary || {}).map((k) => {
                const tot = importSummary.summary[k] || 0;
                const conf = importSummary.conflicts[k] || 0;
                const inv = importSummary.invalid[k] || 0;
                const nuovi = Math.max(0, tot - conf - inv);
                return (
                  <div key={k} className="flex flex-col sm:flex-row sm:justify-between border-b border-border/60 pb-1">
                    <span className="font-medium">{COLL_LABELS[k] || k}</span>
                    <span className="font-mono text-xs">{tot} totali · {conf} già presenti · {nuovi} nuovi · {inv} non validi</span>
                  </div>
                );
              })}
              {totalNew === 0
                ? <p className="text-xs text-accent font-semibold mt-2">0 nuovi record da importare — l'import non modificherà nulla.</p>
                : <p className="text-xs text-muted-foreground mt-2">Verranno importati {totalNew} nuovi record. I record già presenti (stesso ID) vengono saltati, mai sovrascritti.</p>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setImportSummary(null); setImportData(null); }} data-testid="import-cancel">Annulla</Button>
            <Button data-testid="backup-import-confirm" onClick={confirmImport} disabled={totalNew === 0}>Importa</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <p className="text-center text-[11px] text-muted-foreground pt-2 pb-6" data-testid="app-version">
        K9 Manager · versione {RELEASE.version}{RELEASE.buildId && RELEASE.buildId !== "dev" ? ` · build ${RELEASE.buildId}` : ""}
      </p>
    </div>
  );
}
